import React, { useEffect, useState } from 'react';
import { PageHeader, Card, StatusPill, Modal, ColorBtn } from '../../components/UI';
import api from '../../api/client';
import T from '../../theme';

const ROLES = [
  { value: 'admin', label: 'Admin', icon: '🛡️', color: '#7C3AED', bg: '#F5F3FF' },
  { value: 'owner', label: 'Ota-ona', icon: '👨‍👩‍👧', color: T.blue, bg: T.blueSoft },
  { value: 'relative', label: 'Qarindosh', icon: '👥', color: '#F59E0B', bg: '#FFFBEB' },
];

// Faqat owner va relative yaratish mumkin (admin faqat Django admin orqali)
const CREATABLE_ROLES = [
  { value: 'owner', label: 'Ota-ona', icon: '👨‍👩‍👧', color: T.blue, bg: T.blueSoft },
  { value: 'relative', label: 'Qarindosh', icon: '👥', color: '#F59E0B', bg: '#FFFBEB' },
];

function UserRow({ user, onEdit, onDelete, isHovered, onHover }) {
  const role = ROLES.find(r => r.value === user.role) || ROLES[1];
  
  return (
    <tr
      onMouseEnter={() => onHover(user.id)}
      onMouseLeave={() => onHover(null)}
      style={{ 
        cursor: 'pointer', 
        background: isHovered ? '#F8FAFC' : 'transparent',
        transition: 'background 0.15s'
      }}
    >
      <td style={tdStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 12,
            background: `linear-gradient(135deg, ${T.blue}, ${T.blueLight})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 16, color: '#fff', fontWeight: 800,
          }}>
            {user.first_name?.[0]?.toUpperCase() || user.username?.[0]?.toUpperCase() || '?'}
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>
              {user.first_name} {user.last_name}
            </div>
            <div style={{ fontSize: 12, color: T.textMuted }}>@{user.username}</div>
          </div>
        </div>
      </td>
      <td style={tdStyle}>
        <span style={{ fontFamily: T.mono, fontSize: 13, color: T.textSec }}>
          {user.phone || '—'}
        </span>
      </td>
      <td style={tdStyle}>
        <span style={{
          padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
          background: role.bg, color: role.color,
        }}>
          {role.icon} {role.label}
        </span>
      </td>
      <td style={tdStyle}>
        <span style={{
          padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
          background: user.is_active ? T.greenSoft : T.coralSoft,
          color: user.is_active ? '#065F46' : '#DC2626',
        }}>
          {user.is_active ? '● Faol' : '○ Nofaol'}
        </span>
      </td>
      <td style={tdStyle}>
        <span style={{ fontSize: 12, color: T.textMuted, fontFamily: T.mono }}>
          {user.date_joined ? new Date(user.date_joined).toLocaleDateString('uz-UZ') : '—'}
        </span>
      </td>
      <td style={tdStyle}>
        <div style={{ display: 'flex', gap: 6 }}>
          {/* Admin foydalanuvchilarni tahrirlash mumkin emas */}
          {user.role !== 'admin' && !user.is_superuser ? (
            <>
              <button onClick={() => onEdit(user)} style={btnStyle}>✏️</button>
              <button onClick={() => onDelete(user)} style={{ ...btnStyle, background: T.coralSoft, color: '#DC2626' }}>🗑</button>
            </>
          ) : (
            <span style={{ fontSize: 11, color: T.textMuted, fontStyle: 'italic' }}>Django admin</span>
          )}
        </div>
      </td>
    </tr>
  );
}

const tdStyle = { padding: '14px 16px', fontSize: 14, borderBottom: `1px solid ${T.borderLight}` };
const btnStyle = { 
  padding: '6px 10px', background: T.bg, border: `1px solid ${T.border}`, 
  borderRadius: 8, cursor: 'pointer', fontSize: 12 
};

function UserModal({ user, open, onClose, onSave }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [username, setUsername] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('owner');
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      setFirstName(user.first_name || '');
      setLastName(user.last_name || '');
      setUsername(user.username || '');
      setPhone(user.phone || '');
      setRole(user.role || 'owner');
      setIsActive(user.is_active !== false);
      setPassword('');
    } else {
      setFirstName('');
      setLastName('');
      setUsername('');
      setPhone('');
      setRole('owner');
      setIsActive(true);
      setPassword('');
    }
    setError('');
  }, [user, open]);

  const handleSubmit = async () => {
    if (!username.trim()) { setError('Login kiritilmagan'); return; }
    if (!user && !password.trim()) { setError('Parol kiritilmagan'); return; }
    
    setLoading(true);
    setError('');
    
    try {
      const data = {
        first_name: firstName,
        last_name: lastName,
        username,
        phone,
        role,
        is_active: isActive,
      };
      if (password) data.password = password;
      
      await onSave(data, user?.id);
      onClose();
    } catch (err) {
      const d = err.response?.data;
      setError(typeof d === 'object' ? Object.values(d).flat().join(', ') : 'Xatolik yuz berdi');
    }
    setLoading(false);
  };

  const inp = {
    width: '100%', padding: '12px 14px', background: T.bg,
    border: `2px solid ${T.border}`, borderRadius: 10, fontSize: 14,
    fontFamily: T.font, fontWeight: 600, color: T.text, outline: 'none',
    boxSizing: 'border-box',
  };

  return (
    <Modal open={open} onClose={onClose} title={user ? '✏️ Foydalanuvchini tahrirlash' : '👤 Yangi foydalanuvchi'}>
      {error && (
        <div style={{ background: T.coralSoft, color: '#DC2626', padding: '10px 14px', borderRadius: 10, fontSize: 13, fontWeight: 600, marginBottom: 16 }}>
          ⚠️ {error}
        </div>
      )}
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={lblStyle}>ISM</label>
            <input style={inp} value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="Ism" />
          </div>
          <div style={{ flex: 1 }}>
            <label style={lblStyle}>FAMILIYA</label>
            <input style={inp} value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Familiya" />
          </div>
        </div>
        
        <div>
          <label style={lblStyle}>LOGIN *</label>
          <input style={inp} value={username} onChange={e => setUsername(e.target.value)} placeholder="login" />
        </div>
        
        <div>
          <label style={lblStyle}>TELEFON</label>
          <input style={{ ...inp, fontFamily: T.mono }} value={phone} onChange={e => setPhone(e.target.value)} placeholder="+998901234567" />
        </div>
        
        <div>
          <label style={lblStyle}>{user ? 'YANGI PAROL (bo\'sh qoldirsa o\'zgarmaydi)' : 'PAROL *'}</label>
          <input style={inp} type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" />
        </div>
        
        <div>
          <label style={lblStyle}>ROL</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {CREATABLE_ROLES.map(r => (
              <button key={r.value} onClick={() => setRole(r.value)} style={{
                flex: 1, padding: '12px 8px', borderRadius: 10,
                background: role === r.value ? r.bg : '#F9FAFB',
                border: `2px solid ${role === r.value ? r.color : T.border}`,
                cursor: 'pointer', fontFamily: T.font, fontSize: 13, fontWeight: 700,
                color: role === r.value ? r.color : T.textMuted,
              }}>
                <div style={{ fontSize: 20, marginBottom: 4 }}>{r.icon}</div>
                {r.label}
              </button>
            ))}
          </div>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={() => setIsActive(!isActive)} style={{
            width: 48, height: 26, borderRadius: 13, border: 'none',
            background: isActive ? T.blue : '#D1D5DB', cursor: 'pointer',
            position: 'relative', transition: 'background 0.2s',
          }}>
            <div style={{
              width: 22, height: 22, borderRadius: '50%', background: '#fff',
              position: 'absolute', top: 2, left: isActive ? 24 : 2,
              transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }} />
          </button>
          <span style={{ fontSize: 14, fontWeight: 600, color: isActive ? T.text : T.textMuted }}>
            {isActive ? 'Faol' : 'Nofaol'}
          </span>
        </div>
        
        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button onClick={onClose} style={{
            flex: 1, padding: '12px', background: '#F9FAFB', border: `2px solid ${T.border}`,
            borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer',
            color: T.textSec, fontFamily: T.font,
          }}>Bekor qilish</button>
          <ColorBtn onClick={handleSubmit} disabled={loading} style={{ flex: 2, borderRadius: 10 }}>
            {loading ? 'Saqlanmoqda...' : '✓ Saqlash'}
          </ColorBtn>
        </div>
      </div>
    </Modal>
  );
}

const lblStyle = { fontSize: 11, fontWeight: 700, color: T.textMuted, display: 'block', marginBottom: 6, letterSpacing: 0.5 };

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [hoveredId, setHoveredId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const loadUsers = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;
      const res = await api.get('/users/', { params });
      setUsers(res.data.results || res.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { loadUsers(); }, [search, roleFilter]);

  const handleSave = async (data, userId) => {
    if (userId) {
      await api.patch(`/users/${userId}/`, data);
    } else {
      await api.post('/users/', data);
    }
    await loadUsers();
  };

  const handleDelete = async (userId) => {
    await api.delete(`/users/${userId}/`);
    setDeleteConfirm(null);
    await loadUsers();
  };

  const openEdit = (user) => { setEditUser(user); setShowModal(true); };
  const openCreate = () => { setEditUser(null); setShowModal(true); };

  const stats = {
    total: users.length,
    admins: users.filter(u => u.role === 'admin').length,
    owners: users.filter(u => u.role === 'owner').length,
    relatives: users.filter(u => u.role === 'relative').length,
  };

  return (
    <div>
      <PageHeader 
        title="👥 Foydalanuvchilar" 
        subtitle={`Jami ${stats.total} ta foydalanuvchi`}
        action={
          <button onClick={openCreate} style={{
            padding: '10px 20px', background: T.blue, color: '#fff',
            border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700,
            cursor: 'pointer', fontFamily: T.font,
          }}>＋ Yangi foydalanuvchi</button>
        }
      />

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Jami', value: stats.total, icon: '👥', color: T.blue, bg: T.blueSoft },
          { label: 'Adminlar', value: stats.admins, icon: '🛡️', color: '#7C3AED', bg: '#F5F3FF' },
          { label: 'Ota-onalar', value: stats.owners, icon: '👨‍👩‍👧', color: '#065F46', bg: '#ECFDF5' },
          { label: 'Qarindoshlar', value: stats.relatives, icon: '👤', color: '#F59E0B', bg: '#FFFBEB' },
        ].map((s, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: 12, padding: 16, border: `1px solid ${T.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 20 }}>{s.icon}</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: s.color, background: s.bg, padding: '2px 8px', borderRadius: 10 }}>{s.label}</span>
            </div>
            <div style={{ fontSize: 28, fontWeight: 900, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 14 }}>🔍</span>
          <input
            style={{
              width: '100%', padding: '10px 14px 10px 36px', background: '#fff',
              border: `1px solid ${T.border}`, borderRadius: 10, fontSize: 14,
              fontFamily: T.font, fontWeight: 500, color: T.text, outline: 'none',
              boxSizing: 'border-box',
            }}
            placeholder="Qidirish..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {[
            { v: '', l: 'Barchasi' },
            { v: 'admin', l: '🛡️ Admin' },
            { v: 'owner', l: '👨‍👩‍👧 Ota-ona' },
            { v: 'relative', l: '👤 Qarindosh' },
          ].map(f => (
            <button key={f.v} onClick={() => setRoleFilter(f.v)} style={{
              padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
              background: roleFilter === f.v ? T.blueSoft : '#fff',
              border: `1px solid ${roleFilter === f.v ? T.blueBorder : T.border}`,
              color: roleFilter === f.v ? T.blue : T.textSec,
              cursor: 'pointer', fontFamily: T.font,
            }}>{f.l}</button>
          ))}
        </div>
      </div>

      {/* Table */}
      <Card padding={0}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: T.textMuted }}>Yuklanmoqda...</div>
        ) : users.length === 0 ? (
          <div style={{ padding: '50px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 12, opacity: 0.3 }}>👥</div>
            <p style={{ color: T.textMuted, fontWeight: 600 }}>Foydalanuvchilar topilmadi</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Foydalanuvchi', 'Telefon', 'Rol', 'Holat', 'Sana', ''].map((h, i) => (
                    <th key={i} style={{
                      textAlign: 'left', padding: '12px 16px', fontSize: 11, fontWeight: 700,
                      color: T.textMuted, letterSpacing: 0.5, borderBottom: `1px solid ${T.border}`,
                      background: '#F8FAFC',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <UserRow
                    key={user.id}
                    user={user}
                    onEdit={openEdit}
                    onDelete={setDeleteConfirm}
                    isHovered={hoveredId === user.id}
                    onHover={setHoveredId}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Modals */}
      <UserModal
        user={editUser}
        open={showModal}
        onClose={() => { setShowModal(false); setEditUser(null); }}
        onSave={handleSave}
      />

      {/* Delete confirm */}
      <Modal open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="⚠️ O'chirishni tasdiqlang" maxWidth={400}>
        <p style={{ fontSize: 15, color: T.textSec, marginBottom: 20 }}>
          <strong>{deleteConfirm?.first_name} {deleteConfirm?.last_name}</strong> foydalanuvchisini o'chirmoqchimisiz?
        </p>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setDeleteConfirm(null)} style={{
            flex: 1, padding: '12px', background: '#F9FAFB', border: `2px solid ${T.border}`,
            borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer',
            color: T.textSec, fontFamily: T.font,
          }}>Yo'q</button>
          <button onClick={() => handleDelete(deleteConfirm.id)} style={{
            flex: 1, padding: '12px', background: '#DC2626', border: 'none',
            borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer',
            color: '#fff', fontFamily: T.font,
          }}>Ha, o'chirish</button>
        </div>
      </Modal>
    </div>
  );
}
